import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { AlertCircle, Loader2, MapPin, Search } from "lucide-react";
import clientService from "@/services/clientService";
import addressService from "@/services/addressService";
import { showError } from "@/store/errorStore";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

const EMPTY_ADDRESS = {
  cep: "",
  logradouro: "",
  number: "",
  complemento: "",
  bairro: "",
  localidade: "",
  estado: "",
  uf: "",
  principal: true,
};

const EMPTY_FORM = {
  name: "",
  cpf: "",
  email: "",
  contact: "",
  whatsapp: false,
  hasAddress: false,
  address: EMPTY_ADDRESS,
};

const FieldError = ({ message }) =>
  message ? (
    <p className="text-sm text-red-500 flex items-center gap-1">
      <AlertCircle className="h-4 w-4" />
      {message}
    </p>
  ) : null;

const maskCPF = (value) => {
  const d = String(value || "")
    .replace(/\D/g, "")
    .slice(0, 11);
  if (d.length > 9) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, "$1.$2.$3-$4");
  if (d.length > 6) return d.replace(/(\d{3})(\d{3})(\d{0,3})/, "$1.$2.$3");
  if (d.length > 3) return d.replace(/(\d{3})(\d{0,3})/, "$1.$2");
  return d;
};

const maskPhone = (value) => {
  const d = String(value || "")
    .replace(/\D/g, "")
    .slice(0, 11);
  if (d.length > 10) return d.replace(/(\d{2})(\d{5})(\d{0,4})/, "($1) $2-$3");
  if (d.length > 6) return d.replace(/(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3");
  if (d.length > 2) return d.replace(/(\d{2})(\d{0,5})/, "($1) $2");
  return d;
};

/**
 * ClientFormModal Component
 * Cria ou edita o cliente da empresa (ClientDTO).
 * O ClientDTO tem UM endereço (`address`, AddressDTO com nomes em PT:
 * cep, logradouro, bairro, localidade, uf...). Endereços adicionais são
 * geridos na tela de Endereços.
 */
const ClientFormModal = ({ isOpen, onClose, onSave, client, isSaving }) => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [searchingCEP, setSearchingCEP] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (client) {
      const address = client.address || null;
      setFormData({
        name: client.name || "",
        cpf: maskCPF(client.cpf),
        email: client.email || "",
        contact: maskPhone(client.contact),
        whatsapp: !!client.whatsapp,
        hasAddress: !!address,
        address: address
          ? {
              ...EMPTY_ADDRESS,
              ...address,
              cep: addressService.formatCEP(address.cep || ""),
              principal: address.principal ?? true,
            }
          : EMPTY_ADDRESS,
      });
    } else {
      setFormData(EMPTY_FORM);
    }
    setErrors({});
  }, [isOpen, client]);

  const clearError = (key) => {
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    clearError(field);
  };

  const handleAddressChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      address: { ...prev.address, [field]: value },
    }));
    clearError(`address_${field}`);
  };

  const handleSearchCEP = async () => {
    const cep = formData.address.cep.replace(/\D/g, "");
    if (cep.length !== 8) {
      showError("CEP deve ter 8 dígitos", "CEP Inválido");
      return;
    }
    setSearchingCEP(true);
    try {
      const data = await addressService.searchByCEP(cep);
      setFormData((prev) => ({
        ...prev,
        address: {
          ...prev.address,
          cep: addressService.formatCEP(cep),
          logradouro: data.logradouro || prev.address.logradouro,
          bairro: data.bairro || prev.address.bairro,
          localidade: data.localidade || prev.address.localidade,
          estado: data.estado || prev.address.estado,
          uf: data.uf || prev.address.uf,
        },
      }));
    } catch (error) {
      showError(error.message || "Erro ao buscar CEP", "Erro na Busca de CEP");
    } finally {
      setSearchingCEP(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Nome é obrigatório";
    }
    if (formData.cpf && !clientService.validateCPF(formData.cpf)) {
      newErrors.cpf = "CPF inválido";
    }
    if (formData.email && !clientService.validateEmail(formData.email)) {
      newErrors.email = "Email inválido";
    }
    if (!formData.contact) {
      newErrors.contact = "Telefone é obrigatório";
    } else if (!clientService.validatePhone(formData.contact)) {
      newErrors.contact = "Telefone inválido";
    }

    if (formData.hasAddress) {
      const validation = addressService.validateAddressData(formData.address);
      Object.entries(validation.errors).forEach(([field, message]) => {
        newErrors[`address_${field}`] = message;
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const clientData = {
      name: formData.name.trim(),
      cpf: formData.cpf.replace(/\D/g, ""),
      email: formData.email.trim(),
      contact: formData.contact.replace(/\D/g, ""),
      whatsapp: !!formData.whatsapp,
      // Não enviar null: o back espera string (NPE conhecido)
      code: client?.code || "",
    };

    if (formData.hasAddress) {
      clientData.address = {
        ...(formData.address.id && { id: formData.address.id }),
        ...addressService.buildPayload(formData.address),
      };
    }

    onSave(clientData);
  };

  const addressField = (field, label, props = {}) => (
    <div className={`space-y-2 ${props.wrapperClass || ""}`}>
      <Label htmlFor={`address_${field}`}>
        {label} {props.required && <span className="text-red-500">*</span>}
      </Label>
      <Input
        id={`address_${field}`}
        placeholder={props.placeholder}
        value={formData.address[field] || ""}
        onChange={(e) =>
          handleAddressChange(
            field,
            props.upper ? e.target.value.toUpperCase() : e.target.value
          )
        }
        className={errors[`address_${field}`] ? "border-red-500" : ""}
        maxLength={props.maxLength}
      />
      <FieldError message={errors[`address_${field}`]} />
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {client ? "Editar Cliente" : "Novo Cliente"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Dados Pessoais */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Dados Pessoais</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">
                    Nome Completo <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    placeholder="Ex: João Silva"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    className={errors.name ? "border-red-500" : ""}
                  />
                  <FieldError message={errors.name} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cpf">CPF</Label>
                  <Input
                    id="cpf"
                    placeholder="000.000.000-00"
                    value={formData.cpf}
                    onChange={(e) => handleChange("cpf", maskCPF(e.target.value))}
                    className={errors.cpf ? "border-red-500" : ""}
                    maxLength={14}
                  />
                  {errors.cpf ? (
                    <FieldError message={errors.cpf} />
                  ) : (
                    <p className="text-xs text-gray-500">
                      Sem CPF o cliente não pode ser excluído.
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="email@exemplo.com"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    className={errors.email ? "border-red-500" : ""}
                  />
                  <FieldError message={errors.email} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact">
                    Telefone <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="contact"
                    placeholder="(00) 00000-0000"
                    value={formData.contact}
                    onChange={(e) =>
                      handleChange("contact", maskPhone(e.target.value))
                    }
                    className={errors.contact ? "border-red-500" : ""}
                    maxLength={15}
                  />
                  <FieldError message={errors.contact} />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Switch
                  id="whatsapp"
                  checked={formData.whatsapp}
                  onCheckedChange={(checked) => handleChange("whatsapp", checked)}
                />
                <Label htmlFor="whatsapp">O telefone tem WhatsApp</Label>
              </div>
            </CardContent>
          </Card>

          {/* Endereço */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Endereço
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Switch
                    id="hasAddress"
                    checked={formData.hasAddress}
                    onCheckedChange={(checked) =>
                      handleChange("hasAddress", checked)
                    }
                  />
                  <Label htmlFor="hasAddress" className="text-sm">
                    Informar endereço
                  </Label>
                </div>
              </div>
            </CardHeader>
            {formData.hasAddress && (
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="address_cep">
                      CEP <span className="text-red-500">*</span>
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        id="address_cep"
                        placeholder="00000-000"
                        value={formData.address.cep}
                        onChange={(e) =>
                          handleAddressChange(
                            "cep",
                            e.target.value.replace(/[^\d-]/g, "")
                          )
                        }
                        className={errors.address_cep ? "border-red-500" : ""}
                        maxLength={9}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleSearchCEP}
                        disabled={searchingCEP}
                        title="Buscar CEP"
                      >
                        {searchingCEP ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Search className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <FieldError message={errors.address_cep} />
                  </div>

                  {addressField("logradouro", "Rua", {
                    required: true,
                    placeholder: "Nome da rua",
                    wrapperClass: "md:col-span-2",
                  })}
                  {addressField("number", "Número", {
                    required: true,
                    placeholder: "123",
                  })}
                  {addressField("complemento", "Complemento", {
                    placeholder: "Apto, Bloco, etc.",
                    wrapperClass: "md:col-span-2",
                  })}
                  {addressField("bairro", "Bairro", {
                    required: true,
                    placeholder: "Nome do bairro",
                  })}
                  {addressField("localidade", "Cidade", {
                    required: true,
                    placeholder: "Nome da cidade",
                  })}
                  {addressField("uf", "UF", {
                    required: true,
                    placeholder: "SP",
                    maxLength: 2,
                    upper: true,
                  })}
                </div>
                <p className="text-xs text-gray-500">
                  Outros endereços do cliente ficam na tela de Endereços.
                </p>
              </CardContent>
            )}
          </Card>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
              className="bg-[#1b5e20] hover:bg-[#144919] text-white"
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : client ? (
                "Atualizar"
              ) : (
                "Criar"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ClientFormModal;
